import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { Box, Button } from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import { AuthContext } from 'authentication/AuthContext';

export default function ToLocationDataGrid({
  rows,
  columns,
  loading,
  getRowId,
  setTotalTransferVolume,
  isVolumeExceeded,
  maxAllowedVolume,
  setToLocations,
  toLocations,
  gridKey,
  showOnlyLocationsWithVolume,
  setGridKey,
}) {
  const apiRef = useGridApiRef();
  const { userProfile } = useContext(AuthContext);
  const id = `toLocationsDataGrid`;
  const columnVisibilityKey = `${userProfile?.sub}.columnVisibility.${id}`;
  const sortKey = `${userProfile?.sub}.sortModel.${id}`;
  const [initialState, setInitialState] = useState();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem(columnVisibilityKey);
    return saved ? JSON.parse(saved) : {};
  });

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      delete currentState.columns; // Remove column visibility from main state
      localStorage.setItem(`${userProfile?.sub}.dataGridState.${id}`, JSON.stringify(currentState));
    }
  }, [apiRef, id, userProfile]);

  const resetSnapShot = () => {
    localStorage?.removeItem(`${userProfile?.sub}.dataGridState.${id}`);
    localStorage?.removeItem(columnVisibilityKey);
    localStorage?.removeItem(sortKey);
    setInitialState({});
    setColumnVisibilityModel({});
    setGridKey(prev => prev + 1);
    if (!apiRef.current) return;
    const currentRef = apiRef.current;
    const allRows = Array.from(currentRef.getRowModels().values());
    setToLocations(...allRows);
  };

  const calculateTotalVolume = useCallback(rowData => {
    const rowsArray = Array.isArray(rowData) ? rowData : [];
    return rowsArray.reduce((sum, row) => {
      const value =
        typeof row.transferVolume === 'string'
          ? parseInt(row.transferVolume, 10)
          : Number(row.transferVolume);
      return !Number.isNaN(value) && value > 0 ? sum + value : sum;
    }, 0);
  }, []);

  // Recalculate from grid API
  const recalculateFromGrid = useCallback(() => {
    if (!apiRef.current) return;

    const allRows = Array.from(apiRef.current.getRowModels().values());
    const total = calculateTotalVolume(allRows);

    setTotalTransferVolume(total);
  }, [apiRef, calculateTotalVolume, setTotalTransferVolume]);

  useEffect(() => {
    if (toLocations.length === 0) return;
    const total = calculateTotalVolume(toLocations);
    setTotalTransferVolume(total);
  }, [calculateTotalVolume, setTotalTransferVolume, toLocations]);

  const handleCellEditStart = useCallback(
    (params, event) => {
      if (params.field === 'transferVolume') {
        const cellIsEmpty = !params.row.transferVolume;
        if (isVolumeExceeded && cellIsEmpty) {
          event.preventDefault();
        }
      }
    },
    [isVolumeExceeded]
  );
  const handleCellEditCommit = useCallback(
    params => {
      if (params.field === 'transferVolume') {
        const newValue = Number(params.value) || 0;
        const currentTotal =
          calculateTotalVolume(Array.from(apiRef.current.getRowModels().values())) -
          (Number(params.row.transferVolume) || 0);

        if (currentTotal + newValue > maxAllowedVolume && params.row.transferVolume) {
          apiRef.current.setEditCellValue({
            field: 'transferVolume',
            id: params.id,
            value: '',
          });
          return;
        }
      }
      setTimeout(recalculateFromGrid, 0);
    },
    [apiRef, calculateTotalVolume, maxAllowedVolume, recalculateFromGrid]
  );

  useEffect(() => {
    if (!apiRef.current) return;
    const currentRef = apiRef.current;
    const unsubscribe = apiRef.current.subscribeEvent('cellEditCommit', handleCellEditCommit);
    // eslint-disable-next-line consistent-return
    return () => {
      const allRows = Array.from(currentRef.getRowModels().values());
      // This is done at cleanup to ensure that the latest state is set
      setToLocations(allRows);
      // Calculate and set total before unmounting
      const total = calculateTotalVolume(allRows);
      setTotalTransferVolume(total);
      unsubscribe();
    };
  }, [apiRef, handleCellEditCommit, setToLocations, calculateTotalVolume, setTotalTransferVolume]);

  const columnsWithValidation = useMemo(() => {
    return columns.map(col => {
      if (col.field === 'transferVolume') {
        return {
          ...col,
          renderEditCell: params =>
            col.renderEditCell({
              ...params,
              isVolumeExceeded,
              maxAllowedVolume,
              totalTransferVolume: calculateTotalVolume(
                Array.from(apiRef.current.getRowModels().values())
              ),
            }),
          valueSetter: params =>
            col.valueSetter({
              ...params,
              isVolumeExceeded,
              maxAllowedVolume,
              totalTransferVolume: calculateTotalVolume(
                Array.from(apiRef.current.getRowModels().values())
              ),
            }),
        };
      }
      return col;
    });
  }, [apiRef, columns, isVolumeExceeded, maxAllowedVolume, calculateTotalVolume]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(`${userProfile?.sub}.dataGridState.${id}`);
    setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});
    window.addEventListener('beforeunload', saveSnapshot);
    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [id, saveSnapshot, userProfile]);

  useEffect(() => {
    if (!apiRef.current) return;
    if (!showOnlyLocationsWithVolume) {
      apiRef.current.setFilterModel({
        items: [
          {
            field: 'totalVolume',
            operator: '!=',
            value: 0,
          },
        ],
      });
    } else {
      apiRef.current.setFilterModel({
        items: [],
      });
    }
  }, [apiRef, showOnlyLocationsWithVolume]);

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton variant="dataTable" />
      <GridToolbarDensitySelector variant="dataTable" />
      <Button onClick={resetSnapShot} startIcon={<RefreshRounded />} variant="dataTable">
        Reset View
      </Button>
      <GridToolbarQuickFilter sx={{ position: 'absolute', right: 0 }} />
    </GridToolbarContainer>
  );

  const handleColumnVisibilityModelChange = useCallback(
    newModel => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem(columnVisibilityKey, JSON.stringify(newModel));
    },
    [columnVisibilityKey]
  );

  const [sortModel, setSortModel] = useState(() => {
    const saved = localStorage.getItem(sortKey);
    return saved ? JSON.parse(saved) : [];
  });

  const handleSortModelChange = useCallback(
    newModel => {
      setSortModel(newModel);
      localStorage.setItem(sortKey, JSON.stringify(newModel));
    },
    [sortKey]
  );
  return (
    <>
      <Box
        sx={{
          '& .volume-exceeded': {
            '&:hover': {
              backgroundColor: 'error.light',
            },
            backgroundColor: 'error.light',
          },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
          width: '100%',
        }}
      >
        <DataGridPremium
          aggregationModel={{
            transferVolume: 'sum',
          }}
          aggregationRowsScope="all"
          apiRef={apiRef}
          autoHeight
          columns={columnsWithValidation}
          columnVisibilityModel={columnVisibilityModel}
          disableColumnMenu
          disableRowSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          getRowId={getRowId}
          getRowSpacing={() => ({
            bottom: 0,
            top: 0,
          })}
          hideFooter
          hideFooterPagination
          hideFooterRowCount
          hideFooterSelectedRowCount
          id="toLocationGrid"
          initialState={{
            ...initialState,
          }}
          isCellEditable={params => {
            if (params.field === 'transferVolume') {
              const cellIsEmpty = !params.row.transferVolume;
              return !(isVolumeExceeded && cellIsEmpty);
            }
            return true;
          }}
          key={gridKey}
          loading={loading}
          onCellEditCommit={handleCellEditCommit}
          onCellEditStart={handleCellEditStart}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onSortModelChange={handleSortModelChange}
          processRowUpdate={newRow => {
            setTimeout(recalculateFromGrid, 0);
            return newRow;
          }}
          rows={toLocations.length > 0 ? toLocations : rows}
          slotProps={{
            aggregationFooter: {
              position: 'bottom',
            },
          }}
          slots={{
            aggregationFooter: true,
            toolbar: CustomToolbar,
          }}
          sortModel={sortModel}
        />
      </Box>
    </>
  );
}

ToLocationDataGrid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getRowId: PropTypes.func,
  gridKey: PropTypes.number.isRequired,
  isVolumeExceeded: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  maxAllowedVolume: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setGridKey: PropTypes.func.isRequired,
  setToLocations: PropTypes.func.isRequired,
  setTotalTransferVolume: PropTypes.func.isRequired,
  showOnlyLocationsWithVolume: PropTypes.bool.isRequired,
  toLocations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

ToLocationDataGrid.defaultProps = {
  getRowId: row => row.id,
  loading: false,
};
